import React, { useState } from 'react';

const IndianSubsidiaryRegistration = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with Indian Subsidiary registration.',
    website_hp: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with Indian Subsidiary registration.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-12">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Indian Subsidiary Company Registration
            </h1>
            <p className="mt-4 text-xl text-blue-100">
              The preferred and most compliant way for a foreign entity to establish a business presence in India.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">🌍</span> Launch Your India Operations
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Requirements</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Registration Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">Foreign Documents</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#you-get">What You Get</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#service-faqs">FAQs</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                What is an Indian Subsidiary?
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                An <strong>Indian Subsidiary</strong> is essentially a Private Limited Company incorporated in India, where a <strong>foreign company holds more than 50% of the paid-up share capital</strong>. It is treated as a separate legal entity from its foreign holding company and must comply with all Indian laws, including the Companies Act, 2013 and FEMA regulations.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Establishing an Indian Subsidiary is the most common and advantageous entry strategy for foreign investors as it offers maximum control, limited liability, and the ability to raise capital and execute contracts in the Indian market as a local entity.
              </p>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Requirements & Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Minimum Directors:</strong> Minimum of <strong>2 directors</strong> (at least one director must be an Indian Resident).
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Foreign Ownership:</strong> Parent company must hold <strong>&gt;50%</strong> of the share capital. Up to 100% FDI is allowed in most sectors via the Automatic Route.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Limited Liability:</strong> Protects the foreign parent company from the subsidiary's operational risks and debts.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Easy Fund Transfer:</strong> Simplifies remittance, capital injection, and repatriation of profits under FEMA guidelines.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Maximum Control:</strong> Allows the foreign parent company to have complete operational and management control.
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-28">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Quick Enquiry
                </h3>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name (Foreign Company)" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number (Include Country Code)" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help setting up an Indian Subsidiary." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      'Request an Initial Consultation'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Procedure Section */}
      <section id="procedure" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              End-to-End International Setup
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              9 Steps for Indian Subsidiary Registration
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { number: "01", icon: "📋", title: "Document Apostille/Attestation", desc: "Obtaining notarization, apostille/consular attestation of foreign company documents and KYC." },
              { number: "02", icon: "🔒", title: "Procure DSC & DIN", desc: "Application and procurement of DSC and DIN for all proposed directors (including foreign directors)." },
              { number: "03", icon: "🏢", title: "Name Reservation (RUN)", desc: "Filing Form RUN to reserve the proposed name with the Registrar of Companies (ROC)." },
              { number: "04", icon: "📄", title: "Drafting MoA & AoA", desc: "Preparation of the Memorandum (MoA) and Articles (AoA) in compliance with both Indian and foreign company requirements." },
              { number: "05", icon: "✍️", title: "Board Resolution & POA", desc: "Drafting and signing of the Board Resolution by the foreign parent company and a Power of Attorney (POA)." },
              { number: "06", icon: "📤", title: "Filing Incorporation Form (SPICe+)", desc: "Submission of the complete set of documents, including MoA, AoA, and all certified foreign documents to the ROC." },
              { number: "07", icon: "🖋️", title: "Issuance of CoI", desc: "The ROC issues the Certificate of Incorporation (CoI), PAN, and TAN for the Indian Subsidiary." },
              { number: "08", icon: "🏦", title: "Open Bank Account & FDI Compliance", desc: "Opening a bank account in India and receiving the share capital contribution (FDI) from the foreign parent." },
              { number: "09", icon: "📊", title: "FEMA Reporting (Form FC-GPR)", desc: "Mandatory filing with the RBI (through AD Category-I bank) regarding the receipt of Foreign Direct Investment (FDI)." }
            ].map((step, index) => (
              <div key={index} className="p-6 bg-white rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow border-t-4 border-blue-500 h-full">
                <div className="procedure-step-content">
                  <div>
                    <span className="text-2xl font-extrabold text-blue-600 block mb-2">{step.number}</span>
                    <span className="text-2xl mx-auto mb-2 block">{step.icon}</span>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mt-3">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Mandatory Attested Documents
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Foreign Holding Company Documentation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "🌍", title: "Parent Company Registration Proof", desc: "Certified copy of the Certificate of Incorporation/Registration of the foreign holding company." },
              { icon: "📊", title: "Memorandum & Articles of Parent", desc: "Certified copy of the Charter/MOA & AOA of the foreign company." },
              { icon: "📋", title: "Board Resolution", desc: "Resolution of the foreign parent board authorizing the incorporation and appointing a signatory for the Indian Subsidiary." },
              { icon: "🆔", title: "KYC of Foreign Directors", desc: "Passport and Proof of Address (bank statement/utility bill) of all foreign directors. Must be apostilled/attested." },
              { icon: "📍", title: "Registered Office Proof (India)", desc: "NOC from the owner and latest Utility Bill (must be less than 30 days old) for the Indian office address." },
              { icon: "⚖️", title: "Power of Attorney (POA)", desc: "A duly notarized and apostilled Power of Attorney in favor of the authorized representative/professional in India." }
            ].map((doc, index) => (
              <div key={index} className="p-6 border rounded-xl shadow-md flex items-start space-x-4 h-full">
                <span className="text-2xl flex-shrink-0 mt-1">{doc.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="you-get" className="py-20 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                  <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
                      Your Deliverables
                  </h2>
                  <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                      What Akir Advisory Delivers
                  </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {[
                    { icon: "📋", title: "Certificate of Incorporation", desc: "Official proof of registration of your Indian Subsidiary as a Private Limited Company." },
                    { icon: "🌍", title: "FDI Compliance & Reporting", desc: "Completion of the mandatory FC-GPR filing with the RBI after capital infusion." },
                    { icon: "🆔", title: "Company PAN and TAN", desc: "The Permanent Account Number and Tax Deduction Account Number for the Indian entity." },
                    { icon: "🏦", title: "Bank Account Guidance", desc: "Assistance in opening the corporate bank account for the subsidiary to receive the FDI." }
                  ].map((item, index) => (
                    <div key={index} className="p-6 bg-white rounded-xl shadow-lg border-l-4 border-green-500 h-full flex flex-col">
                      <span className="text-2xl mb-3 block">{item.icon}</span>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  ))}
              </div>
          </div>
      </section>

      {/* FAQs Section */}
      <section id="service-faqs" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                  <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
                      Common Questions
                  </h2>
                  <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                      Indian Subsidiary FAQs
                  </p>
              </div>
              
              <div className="space-y-4 max-w-4xl mx-auto">
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">Is it mandatory to have an Indian Resident Director?</h4>
                      <p className="text-gray-600">Yes. The Companies Act, 2013 mandates that every company must have at least one director who stays in India for a minimum period of <strong>182 days</strong> in the immediately preceding financial year.</p>
                  </div>
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">What is the required level of attestation for foreign documents?</h4>
                      <p className="text-gray-600">All documents originating from outside India must be notarized, then apostilled (if the country is a Hague Convention member) or consularly attested (by the Indian embassy/consulate in that country).</p>
                  </div>
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">Is there a minimum capital requirement?</h4>
                      <p className="text-gray-600">No, the concept of minimum paid-up capital (like Rs. 1 Lakh for Private Ltd) has been abolished. The capital can be any amount, but the holding company must own more than 50%.</p>
                  </div>
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">What is Form FC-GPR?</h4>
                      <p className="text-gray-600">Form FC-GPR (Foreign Currency - Gross Provisional Return) is a mandatory form filed with the RBI through the Authorised Dealer (AD) Category-I bank within 30 days of the company receiving Foreign Direct Investment (FDI) from its parent.</p>
                  </div>
              </div>
          </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-blue-700/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white">
                Ready to Establish Your Indian Subsidiary?
            </h2>
            <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
                Ensure 100% compliance with both MCA and RBI regulations for your smooth entry into the Indian market.
            </p>
            <a href="#contact-section" className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                <span className="mr-2">📞</span> Consult Our Expert Today
            </a>
        </div>
      </section>
    </main>
  );
};

export default IndianSubsidiaryRegistration;